import { Close } from '@mui/icons-material';
import { Badge, Box, Button, Chip, CircularProgress, IconButton, MenuItem, SwipeableDrawer, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import FilterIcon from 'assets/images/FilterIcon.svg';
import CustomButtonWithIconComponent from 'components/CustomButtonWithIcon/Buttons';
import CustomOutlinedInput from 'components/CustomOutlinedInput/CustomOutlinedInput';
import CustomFormControl from 'features/bidding-center/Dropdown/CustomFormControl';
import { useEffect, useRef, useState } from 'react';
import { MultiSelectValueDropdown, VirtualizedValueDropdown } from './ValueDropdowns';

/**
 * Shared reporting table filters drawer + applied-filter chips.
 *
 * Product-agnostic: consumes the `columnOptions` (metrics + segments, with
 * `allowedOps`/`filterValues`/`isActive` metadata) produced by the shared
 * `useReportingFilters` hook. Used by every reporting table across the app
 * (search ads, PMax, display ads, meta travel ads, AI connect, metasearch).
 */

// ─── Operator definitions ────────────────────────────────────────────────────

const NUMERIC_OPERATORS = {
    gt:           "> Greater than",
    gte:          ">= Greater than or equal to",
    lt:           "< Less than",
    lte:          "<= Less than or equal to",
    eq:           "Is",
    neq:          "Is Not",
    between:      "↔ Between",
};

const TEXT_OPERATORS = {
    eq:           "Is",
    neq:          "Is Not",
    contains:     "⊃ Contains",
};

// Master label map — used to resolve labels for operators coming from the
// metadata `allowed_ops`, regardless of field type.
const OPERATOR_LABELS = { ...NUMERIC_OPERATORS, ...TEXT_OPERATORS };

const NO_VALUE_OPS  = new Set(["is_blank", "is_not_blank"]);
const TWO_VALUE_OPS = new Set(["between"]);
// eq / neq accept an array of values (IN / NOT IN). When the column exposes a
// value catalog we let the user pick multiple values at once.
const MULTI_VALUE_OPS = new Set(["eq", "neq"]);

const isNumeric = (col) =>
    col?.fieldType === "numeric" || col?.fieldType === "percent" || col?.type === "METRICS";

// Resolve the operator list for a column. Prefer the metadata-provided
// `allowedOps`; fall back to the static numeric/text catalogs otherwise.
const getOperatorsForColumn = (col) => {
    if (col?.allowedOps?.length) {
        return col.allowedOps.reduce((acc, op) => {
            acc[op] = OPERATOR_LABELS[op] || op;
            return acc;
        }, {});
    }
    return isNumeric(col) ? NUMERIC_OPERATORS : TEXT_OPERATORS;
};

const inputSx     = { height: "28px", backgroundColor: "white", fontSize: "12px" };

// ─── Operator label for chips ─────────────────────────────────────────────────

const getOperatorLabel = (op) => OPERATOR_LABELS[op] || op;

// Resolve the human-readable label for a catalog value (e.g. DEVICE -> "Mobile")
const getValueLabel = (col, value) => {
    if (!col?.filterValues?.length) return value;
    return col.filterValues.find(v => String(v.value) === String(value))?.label ?? value;
};

// ─── Human-readable chip label ────────────────────────────────────────────────

const buildChipLabel = (rule, columnOptions) => {
    const col      = columnOptions.find(c => c.field === rule.field);
    const colLabel = col?.label || rule.field;
    const opLabel  = getOperatorLabel(rule.op);

    if (NO_VALUE_OPS.has(rule.op))  return `${colLabel} ${opLabel}`;
    if (TWO_VALUE_OPS.has(rule.op)) return `${colLabel} (between ${rule.value} to ${rule.value_to})`;

    // Multiple values (eq / neq with an array) → list the labels.
    if (Array.isArray(rule.value)) {
        const labels = rule.value.map(v => getValueLabel(col, v));
        if (labels.length <= 2) return `${colLabel} ${opLabel} ${labels.join(", ")}`;
        return `${colLabel} ${opLabel} ${labels.slice(0, 2).join(", ")} +${labels.length - 2} more`;
    }

    return `${colLabel} ${opLabel} ${getValueLabel(col, rule.value)}`;
};

// ─── Validation ───────────────────────────────────────────────────────────────

const validateFilters = (filtersList) => {
    const errors = {};
    filtersList.forEach((f, i) => {
        if (!f.field) {
            errors[i] = { field: "Column is required" };
            return;
        }
        if (!f.op) {
            errors[i] = { op: "Option is required" };
            return;
        }
        if (!NO_VALUE_OPS.has(f.op)) {
            const isEmptyValue = Array.isArray(f.value)
                ? f.value.length === 0
                : (f.value === "" || f.value === undefined || f.value === null);
            if (isEmptyValue) {
                errors[i] = { value: "Value is required" };
                return;
            }
            if (TWO_VALUE_OPS.has(f.op) && (f.value_to === "" || f.value_to === undefined || f.value_to === null)) {
                errors[i] = { value_to: "'To' value is required" };
            }
        }
    });
    return errors;
};

const buildPayload = (filtersList, operator) => {
    const rules = filtersList
        .filter(f => f.field && f.op)
        .map(f => {
            const rule = { field: f.field, op: f.op };
            if (!NO_VALUE_OPS.has(f.op)) {
                if (Array.isArray(f.value)) {
                    // Multi-select (eq / neq → IN / NOT IN). Keep catalog values
                    // as-is, coercing numeric-looking entries to numbers.
                    rule.value = f.value.map(v =>
                        (v === "" || v === null || isNaN(v)) ? v : Number(v)
                    );
                } else {
                    rule.value = isNaN(f.value) || f.value === "" ? f.value : Number(f.value);
                }
                if (TWO_VALUE_OPS.has(f.op)) {
                    rule.value_to = isNaN(f.value_to) || f.value_to === "" ? f.value_to : Number(f.value_to);
                }
            }
            return rule;
        });

    if (!rules.length) return [];
    return [{ combinator: operator.toLowerCase(), rules }];
};

// ─── FilterItem ───────────────────────────────────────────────────────────────

const FilterItem = ({ columnOptions, filter, index, onChangeFilter, onRemove, operator, onOperatorChange, showOperator, error }) => {
    const selectedCol  = columnOptions.find(c => c.field === filter.field);
    const operators    = getOperatorsForColumn(selectedCol);
    const needsNoValue = NO_VALUE_OPS.has(filter.op);
    const needsTwoVal  = TWO_VALUE_OPS.has(filter.op);
    const valueCatalog = selectedCol?.filterValues || [];
    const isFreeTextOp = filter.op === "contains";
    const hasCatalog   = valueCatalog.length > 0 && !needsTwoVal && !isFreeTextOp;
    const isMultiSelect = hasCatalog && MULTI_VALUE_OPS.has(filter.op);

    return (
        <>
            {showOperator && (
                <Box display="flex" justifyContent="center" alignItems="center" my="4px">
                    <ToggleButtonGroup
                        value={operator}
                        exclusive
                        onChange={(_, val) => { if (val) onOperatorChange(val); }}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                padding: '2px 14px',
                                fontSize: '10px',
                                fontFamily: 'Inter',
                                fontWeight: 600,
                                color: '#BAB7B7',
                                border: '1px solid #C4C4C4',
                                textTransform: 'uppercase',
                                lineHeight: 'normal',
                                '&.Mui-selected': {
                                    backgroundColor: '#61D5A9',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: '#4ec99d' },
                                },
                                '&:hover': { backgroundColor: '#f5f5f5' },
                            },
                        }}
                    >
                        <ToggleButton value="AND">AND</ToggleButton>
                        <ToggleButton value="OR">OR</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            <Box display="flex" flexDirection="column" gap="4px" border="1px solid lightgray" borderRadius="12px" padding="10px" position="relative">
                {/* Remove button — top right corner */}
                <IconButton
                    onClick={() => onRemove(index)}
                    size="small"
                    sx={{ position: "absolute", top: 4, right: 4, padding: "2px" }}
                >
                    <Close sx={{ fontSize: "12px", color: "#BAB7B7" }} />
                </IconButton>

                {/* Column */}
                <Box sx={{ flex: 1, pr: "24px" }}>
                    <CustomFormControl
                        minWidth={"380px"}
                        value={filter.field || ""}
                        onChange={e => onChangeFilter("field", e.target.value, index)}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) return <span style={{ fontSize: "12px", color: error?.field ? "#d32f2f" : "#bab7b7" }}>Select Column</span>;
                            return columnOptions.find(c => c.field === selected)?.label || selected;
                        }}
                        required
                        helperText={error?.field || "Column"}
                        helperTextFontSize="10px"
                        error={!!error?.field}
                        sx={{ "& .MuiOutlinedInput-notchedOutline": error?.field ? { borderColor: "#d32f2f !important" } : {} }}
                    >
                        <MenuItem disabled value="">
                            <span style={{ fontSize: "12px", color: "#bab7b7", marginLeft: "4px" }}>Select Column</span>
                        </MenuItem>
                        {columnOptions.map((option) => {
                            const { field, label, isActive } = option;
                            const disabled = isActive === false;
                            return (
                                <MenuItem key={field} value={field} disabled={disabled} sx={disabled ? { "&.Mui-disabled": { opacity: 1 } } : {}}>
                                    <span style={{ fontSize: "12px", color: disabled ? "#868e96" : "#373F41", marginLeft: "4px" }}>
                                        {label}
                                    </span>
                                    {disabled && (
                                        <span style={{ fontSize: "10px", color: "#9ba1a6", marginLeft: "8px", fontStyle: "italic" }}>
                                            (add segment to filter)
                                        </span>
                                    )}
                                </MenuItem>
                            );
                        })}
                    </CustomFormControl>
                </Box>

                {/* Operator */}
                {(filter.field || error?.op) && (
                    <CustomFormControl
                        minWidth={"221px"}
                        value={filter.op || ""}
                        onChange={e => onChangeFilter("op", e.target.value, index)}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) return <span style={{ fontSize: "12px", color: error?.op ? "#d32f2f" : "#bab7b7" }}>Select Option</span>;
                            return operators[selected];
                        }}
                        required
                        helperText={error?.op || "Option"}
                        helperTextFontSize="10px"
                        error={!!error?.op}
                        sx={{ "& .MuiOutlinedInput-notchedOutline": error?.op ? { borderColor: "#d32f2f !important" } : {} }}
                    >
                        <MenuItem disabled value="">
                            <span style={{ fontSize: "12px", color: "#bab7b7", marginLeft: "4px" }}>Select Option</span>
                        </MenuItem>
                        {Object.entries(operators).map(([key, label]) => (
                            <MenuItem key={key} value={key}>
                                <span style={{ fontSize: "12px", color: "#373F41", marginLeft: "4px" }}>{label}</span>
                            </MenuItem>
                        ))}
                    </CustomFormControl>
                )}

                {/* Value(s) */}
                {filter.op && !needsNoValue && (
                    <Box display="flex" gap="8px" alignItems="flex-start" flexWrap="wrap">
                        <Box sx={{ flex: 1, minWidth: '120px', position: 'relative' }}>
                            {hasCatalog ? (
                                    isMultiSelect ? (
                                        <MultiSelectValueDropdown
                                            valueCatalog={valueCatalog}
                                            selectedValues={
                                                Array.isArray(filter.value)
                                                    ? filter.value
                                                    : (filter.value === "" || filter.value === undefined || filter.value === null ? [] : [filter.value])
                                            }
                                            onChange={(vals) => onChangeFilter("value", vals, index)}
                                            error={!!error?.value}
                                        />
                                    ) : (
                                        <VirtualizedValueDropdown
                                            valueCatalog={valueCatalog}
                                            selectedValue={filter.value}
                                            onChange={(val) => onChangeFilter("value", val, index)}
                                            error={!!error?.value}
                                        />
                                    )
                            ) : (
                                <CustomOutlinedInput
                                    minWidth={"0"}
                                    placeholder={needsTwoVal ? "From" : "Value"}
                                    variant="outlined"
                                    value={filter.value ?? ""}
                                    onChange={e => onChangeFilter("value", e.target.value, index)}
                                    FormHelperTextProps={{ className: "custom-helper-text" }}
                                    InputProps={{
                                        sx: {
                                            ...inputSx,
                                            border: error?.value ? "1px solid #d32f2f" : undefined,
                                        }
                                    }}
                                />
                            )}
                            {error?.value && (
                                <span style={{ color: "#d32f2f", fontSize: "10px", fontFamily: "Inter", marginTop: "2px", display: "block" }}>
                                    {error.value}
                                </span>
                            )}
                        </Box>
                        {needsTwoVal && (
                            <Box sx={{ flex: 1, minWidth: '120px' }}>
                                <CustomOutlinedInput
                                    minWidth={"0"}
                                    placeholder="To"
                                    variant="outlined"
                                    value={filter.value_to ?? ""}
                                    onChange={e => onChangeFilter("value_to", e.target.value, index)}
                                    FormHelperTextProps={{ className: "custom-helper-text" }}
                                    InputProps={{
                                        sx: {
                                            ...inputSx,
                                            border: error?.value_to ? "1px solid #d32f2f" : undefined,
                                        }
                                    }}
                                />
                                {error?.value_to && (
                                    <span style={{ color: "#d32f2f", fontSize: "10px", fontFamily: "Inter", marginTop: "2px", display: "block" }}>
                                        {error.value_to}
                                    </span>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </>
    );
};

// ─── Applied filter chips (rendered in the table toolbar) ─────────────────────

export const FilterChips = ({ appliedFilters = [], appliedOperator = "AND", columnOptions = [], onClear, onRemoveFilter, onChipClick }) => {
    if (!appliedFilters.length) return null;

    return (
        <Box display="flex" alignItems="center" gap="4px" ml="4px">
            {/* Separator */}
            <Box sx={{ width: "1px", height: "16px", backgroundColor: "#C4C4C4", mx: "4px", flexShrink: 0 }} />

            {appliedFilters.map((rule, i) => (
                <Box key={i} display="flex" alignItems="center" gap="4px">
                    {i > 0 && (
                        <span style={{
                            fontSize: "10px",
                            fontWeight: 600,
                            color: "#373F41",
                            fontFamily: "Inter",
                            flexShrink: 0,
                            margin: "0 4px",
                        }}>
                            {appliedOperator}
                        </span>
                    )}
                    <Tooltip title={buildChipLabel(rule, columnOptions)} placement="top">
                        <Chip
                            variant="outlined"
                            label={buildChipLabel(rule, columnOptions)}
                            onClick={onChipClick}
                            onDelete={onRemoveFilter ? () => onRemoveFilter(i) : undefined}
                            deleteIcon={
                                <Close
                                    sx={{
                                        fontSize: "14px",
                                        height: "14px",
                                        width: "14px",
                                        marginTop: "4px",
                                    }}
                                />
                            }
                            sx={{
                                fontFamily: "Inter",
                                fontSize: "12px",
                                fontWeight: "400",
                                height: "22px",
                                marginRight: "4px",
                                maxWidth: "240px",
                                backgroundColor: "#EBF9F4",
                                borderColor: "#61D5A9",
                                color: "#373F41",
                                cursor: "pointer",
                                "& .MuiChip-label": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                },
                            }}
                        />
                    </Tooltip>
                </Box>
            ))}

            {/* Clear all button */}
            <Button
                size="small"
                variant="text"
                onClick={onClear}
                sx={{
                    fontSize: "10px",
                    fontFamily: "Inter",
                    fontWeight: 500,
                    color: "#BAB7B7",
                    padding: "0 4px",
                    minWidth: "unset",
                    textTransform: "none",
                    flexShrink: 0,
                    "&:hover": { color: "#373F41", backgroundColor: "transparent" },
                }}
            >
                Clear all
            </Button>
        </Box>
    );
};

// ─── Main Filters component ───────────────────────────────────────────────────

const Filters = ({ onSave, onClear, columnOptions = [], appliedFilters = [], appliedOperator = "AND", isLoading = false, openTrigger = 0 }) => {
    const [filtersList, setFiltersList] = useState([]);
    const [open, setOpen]               = useState(false);
    const [operator, setOperator]       = useState("AND");
    const [errors, setErrors]           = useState({});
    const prevOpenRef                   = useRef(false);

    // Open panel when external trigger changes
    useEffect(() => {
        if (openTrigger > 0) setOpen(true);
    }, [openTrigger]);

    // Synchronize only on the closed-to-open transition. Parent metadata and
    // array identities may change while the user is editing (e.g. a segment
    // refresh); re-syncing then would unexpectedly replace the selected
    // column/operator/value mid-edit.
    useEffect(() => {
        if (open && !prevOpenRef.current) {
            if (appliedFilters.length > 0) {
                setFiltersList(appliedFilters.map(r => ({
                    field:    r.field,
                    op:       r.op,
                    value:    Array.isArray(r.value)
                                  ? r.value
                                  : (r.value !== undefined ? String(r.value) : ""),
                    value_to: r.value_to !== undefined ? String(r.value_to) : "",
                })));
                setOperator(appliedOperator);
            } else {
                setFiltersList([]);
                setOperator("AND");
            }
            setErrors({});
        }
        prevOpenRef.current = open;
    }, [open, appliedFilters, appliedOperator]);

    const handleChangeFilter = (key, value, index) => {
        // Clear error for this item on any change
        setErrors(prev => { const e = { ...prev }; delete e[index]; return e; });
        setFiltersList(prev => prev.map((f, i) => {
            if (i !== index) return f;
            if (key === "field") return { field: value };
            if (key === "op")    return { ...f, op: value, value: "", value_to: "" };
            return { ...f, [key]: value };
        }));
    };

    const handleSave = () => {
        const validationErrors = validateFilters(filtersList);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        const payload = buildPayload(filtersList, operator);
        onSave?.(payload, operator);
        setOpen(false);
    };

    const handleClear = () => {
        setFiltersList([]);
        setOperator("AND");
        setErrors({});
        onSave?.([], "AND");
        onClear?.();
    };

    return (
    <>
            {/* Filter icon with count badge */}
            <Tooltip title="Filters" placement="bottom">
                <IconButton sx={{ padding: "4px", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.12)" } }} onClick={() => setOpen(true)}>
                    <Badge
                        badgeContent={appliedFilters.length || null}
                        sx={{
                            "& .MuiBadge-badge": {
                                backgroundColor: "#61D5A9",
                                color: "#fff",
                                fontSize: "9px",
                                height: "14px",
                                minWidth: "14px",
                                padding: "0 3px",
                            },
                        }}
                    >
                        <img src={FilterIcon} alt="Filters" style={{ width: 19, height: 17 }} />
                    </Badge>
                </IconButton>
            </Tooltip>

            {/* Drawer */}
            <SwipeableDrawer
                anchor="right"
                open={open}
                onClose={() => setOpen(false)}
                onOpen={() => setOpen(true)}
                transitionDuration={{ enter: 500, exit: 350 }}
                SlideProps={{
                    easing: {
                        enter: "cubic-bezier(0.16, 1, 0.3, 1)",
                        exit:  "cubic-bezier(0.7, 0, 0.84, 0)",
                    },
                }}
                PaperProps={{
                    sx: {
                        width: 500,
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: "-4px 0 24px rgba(0,0,0,0.10)",
                    }
                }}
            >
                {/* Header */}
                <Box sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    borderBottom: "1px solid lightgray",
                    padding: "6px 16px",
                    display: "flex",
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}>
                    <span>Filters</span>
                    <IconButton onClick={() => setOpen(false)}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>

                {/* Scrollable content */}
                <Box sx={{ flex: 1, overflowY: 'auto', padding: "12px 24px" }}>
                    {isLoading ? (
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ py: 6, gap: "12px" }}>
                            <CircularProgress size={28} sx={{ color: "#61D5A9" }} />
                            <span style={{ fontSize: "12px", fontFamily: "Inter", color: "#BAB7B7" }}>Loading filter options…</span>
                        </Box>
                    ) : (
                    <Box display="flex" flexDirection="column" gap="8px">
                        {filtersList.map((filter, index) => (
                            <FilterItem
                                key={index}
                                columnOptions={columnOptions}
                                filter={filter}
                                index={index}
                                onChangeFilter={handleChangeFilter}
                                onRemove={(i) => {
                                    setErrors(prev => { const e = { ...prev }; delete e[i]; return e; });
                                    setFiltersList(prev => prev.filter((_, ind) => ind !== i));
                                }}
                                operator={operator}
                                onOperatorChange={setOperator}
                                showOperator={index > 0}
                                error={errors[index]}
                            />
                        ))}
                        {filtersList.length < 10 && (
                            <CustomButtonWithIconComponent
                                color="#b0acac"
                                borderColor="#b0acac"
                                hoverColor="#12794F"
                                titleColor="#373F41"
                                hoverTitleColor="white"
                                height="28px"
                                width="fit-content"
                                borderRadius="20px"
                                textStyle={{
                                    fontFamily: "Inter",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                }}
                                onClick={() => setFiltersList(prev => [...prev, {}])}
                            >
                                Add filter rule
                            </CustomButtonWithIconComponent>
                        )}
                    </Box>
                    )}
                </Box>

                {/* Sticky footer */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: filtersList.length > 0 ? 'space-between' : 'flex-start',
                    padding: "6px 24px",
                    borderTop: "1px solid lightgray",
                    flexShrink: 0,
                    bgcolor: 'white',
                }}>
                    <CustomButtonWithIconComponent
                        color="#b0acac"
                        borderColor="#b0acac"
                        hoverColor="#12794F"
                        titleColor="#373F41"
                        hoverTitleColor="white"
                        height="28px"
                        borderRadius="20px"
                        textStyle={{
                            fontFamily: "Inter",
                            fontSize: "12px",
                            fontWeight: 500,
                        }}
                        disabled={filtersList.length === 0}
                        onClick={handleClear}
                    >
                        Clear all
                    </CustomButtonWithIconComponent>
                    {filtersList.length > 0 && (
                    <CustomButtonWithIconComponent
                        color="#12794F"
                        borderColor="#12794F"
                        hoverColor="#0e5e3d"
                        titleColor="white"
                        hoverTitleColor="white"
                        height="28px"
                        borderRadius="20px"
                        textStyle={{
                            fontFamily: "Inter",
                            fontSize: "12px",
                            fontWeight: 500,
                        }}
                        onClick={handleSave}
                    >
                        Save
                    </CustomButtonWithIconComponent>
                    )}
                </Box>
            </SwipeableDrawer>
        </>
    );
};

export default Filters;
